import React, { useState, useEffect, useCallback } from 'react';
import { FaGlobe, FaSync, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import api from '../../../../services/api'; // Adjust path as needed
import './DomainRegistrationCard.css';
import { Loading } from '../../../common';

const DomainRegistrationCard = ({ showRefresh = false }) => {
  const [domainsList, setDomainsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadDomainsData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/domains');
      // Dynamic unwrap logic preserved from your original code
      const responseData = response?.data?.data ? response.data : response;
      const domains = responseData?.data?.domains || [];
      setDomainsList(domains);
    } catch (err) {
      setError(err.message || 'Server Error fetching domains');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDomainsData();
  }, [loadDomainsData]);

  return (
    <div className="x9q-tenant-domain-registry-card">
      <div className="x9q-tenant-domain-registry-card__header">
        <div className="x9q-tenant-domain-registry-card__header-left">
          <h2>
            Registered Domains
          </h2>
        </div>
        {showRefresh && (
          <button 
            className="tenant-info__action-btn tenant-info__action-btn--secondary"
            onClick={loadDomainsData}
            disabled={loading}
          >
            <FaSync className={loading ? 'fa-spin' : ''} /> Refresh Domains
          </button>
        )}
      </div>

      <div className="x9q-tenant-domain-registry-card__body">
        {loading ? (
            <Loading text='Loading Domains'/>
        ) : error ? (
          <div className="x9q-tenant-domain-registry-card__error">
            <FaTimesCircle />
            <p>{error}</p>
            <button onClick={loadDomainsData} className="x9q-tenant-domain-registry-card__retry-btn">
              Retry Load
            </button>
          </div>
        ) : (
          <div className="x9q-tenant-domain-registry-card__table-wrapper">
            {/* Table Header */}
            <div className="x9q-tenant-domain-registry-card__grid x9q-tenant-domain-registry-card__grid--header">
              <div>Domain Name</div>
              <div>Type</div>
              <div>Password Validity(days)</div>
              <div>Services</div>
              <div>Status</div>
            </div>

            {/* Table Rows */}
            {domainsList?.length > 0 ? (
              domainsList.map((domain, index) => (
                <div key={domain?.id || index} className="x9q-tenant-domain-registry-card__grid x9q-tenant-domain-registry-card__grid--row">
                  <div className="x9q-tenant-domain-registry-card__domain-cell">
                    <span className="x9q-tenant-domain-registry-card__domain-text">{domain?.id}</span>
                    {domain?.isDefault && <span className="x9q-tenant-domain-registry-card__primary-badge">Primary</span>}
                  </div>

                  <div>
                    <span className="x9q-tenant-domain-registry-card__type-text">
                      {domain?.isInitial ? 'Initial' : 'Custom'} ({domain?.authenticationType})
                    </span>
                  </div>

                  <div>
                    <span className="x9q-tenant-domain-registry-card__type-text">
                      {domain?.passwordValidityPeriodInDays || 'N/A'}
                    </span>
                  </div>

                  <div>
                    <div className="x9q-tenant-domain-registry-card__services">
                      {domain?.supportedServices?.length > 0 ? (
                        domain.supportedServices.map((service, sIndex) => (
                          <span key={sIndex} className={`x9q-tenant-domain-registry-card__service-tag x9q-tenant-domain-registry-card__service-tag--${service?.toLowerCase?.()}`}>
                            {service}
                          </span>
                        ))
                      ) : (
                        <span className="x9q-tenant-domain-registry-card__empty-text">None</span>
                      )}
                    </div>
                  </div>

                  <div>
                    {domain?.isVerified ? (
                      <span className="x9q-tenant-domain-registry-card__status-badge x9q-tenant-domain-registry-card__status-badge--active">
                        <FaCheckCircle /> Verified
                      </span>
                    ) : (
                      <span className="x9q-tenant-domain-registry-card__status-badge x9q-tenant-domain-registry-card__status-badge--inactive">
                        <FaTimesCircle /> Unverified
                      </span>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="x9q-tenant-domain-registry-card__empty-state">
                No domains found for this environment setup.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainRegistrationCard;